import {Component} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {NotificationFilterRequest, NotificationsApiService, PagedNotifications} from '@npck/af-frontend-api';
import {DATE_TIME_SERVER_FORMAT, RouterService, rxSpinner, TAB} from '@npck/libs/commons';
import dayjs from 'dayjs';
import {distinctUntilChanged, mergeMap, Observable, Subscription} from 'rxjs';
import {
    fieldExtractor,
    FilterDictionaryField,
    FilterEvent,
    FilterField,
    getFormattedDateOrNull,
} from '../../components/full-table/filter/filter.component';
import {CACHE_EXPIRATION_INTERVAL} from '../../domain/constants';
import {DictionaryHolderService} from '../../domain/dictionary-holder.service';
import {NotificationsService} from '../../domain/notifications.service';
import {Path} from '../../path';
import {initialNotificationEvent, NotificationEvent} from './notification/notification.component';

@Component({
    selector: 'af-notifications',
    templateUrl: './notifications.component.html',
    standalone: false
})
export class NotificationsComponent {
    tabs: NotificationsTab[] = ['tabUnreadNotifications', 'tabAllNotifications'];
    activeTab: NotificationsTab;
    selectedTabIndex: number;

    notificationsCount: number;

    filterFields: FilterField[] = [
        {
            type: 'text',
            name: 'query',
        },
        <FilterDictionaryField>{
            isMultiSelect: true,
            type: 'select',
            name: 'notificationType',
            dictionaryName: 'custom_notification_group_type',
            values: [],
        },
        <FilterField>{
            type: 'dateRange',
            name: 'dateRange',
        },
    ];

    tabNotificationHolder = this.tabs.reduce((acc, item) => {
        if (!acc[item]) {
            acc[item] = <NotificationTabInfo>{
                notificationEvent: {
                    ...initialNotificationEvent,
                    filter: this.getDefaultFilterForTab(item),
                },
            };
        }
        return acc;
    }, {} as Record<NotificationsTab, NotificationTabInfo>);

    private subscription: Subscription;

    constructor(
        private routerService: RouterService,
        private activatedRoute: ActivatedRoute,
        private notificationsApiService: NotificationsApiService,
        private notificationService: NotificationsService,
        private dictionaryHolderService: DictionaryHolderService,
    ) {
        this.activeTab = this.activatedRoute.snapshot.queryParamMap.get(TAB) as NotificationsTab || 'tabUnreadNotifications';
        this.selectedTabIndex = this.tabs.findIndex(tab => tab == this.activeTab);
        this.handleNavigation(this.selectedTabIndex);
        this.notificationService.notificationsSubject
            .pipe(distinctUntilChanged((previous, current) => previous === current))
            .subscribe(count => {
                const increased = this.notificationsCount != null && count > this.notificationsCount;
                this.notificationsCount = count;
                if (increased && this.activeTab === 'tabUnreadNotifications') {
                    this.loadNotifications(this.tabNotificationHolder[this.activeTab]?.notificationEvent, true);
                }
            });
    }

    handleNavigation($event: number): void {
        this.activeTab = this.tabs[$event];
        const newEvent: NotificationEvent = {
            ...initialNotificationEvent,
            filter: this.getDefaultFilterForTab(this.activeTab),
        };
        this.tabNotificationHolder[this.activeTab] = {
            notificationEvent: newEvent,
            response: undefined,
            cachedTime: undefined,
        };
        this.routerService.navigate(
            Path.NOTIFICATIONS,
            {
                queryParams: {[TAB]: this.activeTab},
                queryParamsHandling: 'merge',
            });
        this.loadNotifications(newEvent);
    }

    loadNotifications(event: NotificationEvent, shouldForceUpdate = false, append = false): void {
        this.subscription?.unsubscribe();

        const activeTabState = this.tabNotificationHolder[this.activeTab];
        this.tabNotificationHolder[this.activeTab] = {
            ...activeTabState,
            notificationEvent: {
                ...event,
                filter: {
                    ...event.filter,
                    ...this.getDefaultFilterForTab(this.activeTab),
                },
            },
        };
        const isCacheExpired = dayjs().diff(activeTabState.cachedTime, 'seconds') >= CACHE_EXPIRATION_INTERVAL;
        if (append || !activeTabState.response || activeTabState.notificationEvent !== event || isCacheExpired || shouldForceUpdate) {
            const notificationEvent = this.tabNotificationHolder[this.activeTab].notificationEvent;
            const isUnread = this.activeTab === 'tabUnreadNotifications';
            const selectedTypes = fieldExtractor(notificationEvent.filter.notificationType);
            const requestData = <NotificationFilterRequest>{
                limit: notificationEvent.page.pageSize,
                onlyUnread: isUnread,
                query: notificationEvent.filter.query,
                dateStart: getFormattedDateOrNull(notificationEvent.filter?.dateRange?.dateFrom, DATE_TIME_SERVER_FORMAT),
                dateEnd: getFormattedDateOrNull(notificationEvent.filter?.dateRange?.dateTo, DATE_TIME_SERVER_FORMAT),
            };
            if (isUnread) {
                requestData.beforeId = append ? activeTabState.response?.nextBeforeId : undefined;
            } else {
                requestData.offset = notificationEvent.page.pageOffset;
            }
            let request: Observable<PagedNotifications>;
            if (selectedTypes?.length) {
                request = this.dictionaryHolderService.getReference('custom_notification_type')
                    .pipe(mergeMap(dictionary => {
                        const types = dictionary.filter(item => selectedTypes.includes(item.code2)).map(item => item.code);
                        return this.notificationsApiService.getNotifications({...requestData, type: types});
                    }));
            } else {
                request = this.notificationsApiService.getNotifications(requestData);
            }

            this.subscription = request
                .pipe(rxSpinner(notificationEvent.spinnerId))
                .subscribe(response => {
                        const prev = this.tabNotificationHolder[this.activeTab];
                        const merged: PagedNotifications = append
                            ? {...response, items: [...(prev.response?.items ?? []), ...(response.items ?? [])]}
                            : response;
                        this.tabNotificationHolder[this.activeTab] =
                            {...prev, response: merged, cachedTime: dayjs()};
                    },
                );
        }
    }

    loadMoreUnread(): void {
        const state = this.tabNotificationHolder[this.activeTab];
        if (this.activeTab !== 'tabUnreadNotifications' || state.response?.nextBeforeId == null) {
            return;
        }
        this.loadNotifications(state.notificationEvent, false, true);
    }

    private getDefaultFilterForTab(tab: NotificationsTab): FilterEvent {
        if (tab === 'tabUnreadNotifications') {
            return <FilterEvent>{
                notificationStatus: {'not viewed': true},
            };
        }
        return null;
    }
}

interface NotificationTabInfo {
    notificationEvent: NotificationEvent
    response?: PagedNotifications;
    cachedTime: dayjs.Dayjs;
}

export declare type NotificationsTab = 'tabUnreadNotifications' | 'tabAllNotifications';
